/**
 * AdminEmailTester — small admin console panel that lets a super-admin
 * send a deliverability test email to any address (typically the
 * newly-provisioned support mailboxes on sibling sites).
 *
 * Backend: POST /api/admin/email/send-test — see routes/admin_email.py.
 * Returns the Resend message id inline so we can copy/paste it into
 * Resend's dashboard when triaging bounces.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Copy, Check } from "lucide-react";

const PRESETS = [
  {
    id: "ift",
    label: "IndianFamilyTree.com",
    accent: "#B25D38",
    to: "support@indianfamilytree.com",
  },
  {
    id: "mftcare",
    label: "MyFamilyTree.care",
    accent: "#0E7C55",
    to: "support@myfamilytree.care",
  },
  {
    id: "sp",
    label: "Social Pulse",
    accent: "#7C3AED",
    to: "support@socialpulse.com",
  },
];

export default function AdminEmailTester() {
  const [to, setTo] = useState("");
  const [label, setLabel] = useState("Social Pulse");
  const [accent, setAccent] = useState("#7C3AED");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const applyPreset = (p) => {
    setTo(p.to);
    setLabel(p.label);
    setAccent(p.accent);
    setSubject(`[Test] ${p.label} deliverability`);
  };

  const send = async () => {
    if (!to.trim()) return toast.error("Please enter a recipient email.");
    setBusy(true);
    try {
      const { data } = await http.post("/admin/email/send-test", {
        to: to.trim(),
        label: label.trim() || "Social Pulse",
        accent: accent.trim() || "#7C3AED",
        subject: subject.trim() || undefined,
        note: note.trim() || undefined,
      });
      const entry = {
        at: new Date().toISOString(),
        to: data.to || to,
        subject: data.subject,
        label: data.label,
        resend_id: data.id || null,
        status: data.status,
      };
      setHistory((h) => [entry, ...h].slice(0, 20));
      toast.success(`Sent · Resend id ${data.id || "n/a"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const copyId = async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {}
  };

  return (
    <div data-testid="admin-email-tester">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl">Email Deliverability Tester</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
        Send a styled test email to any mailbox to confirm Resend + DNS + inbox
        routing are all working. Every send is logged to <code>admin_events</code>
        with the Resend message id for later triage.
      </p>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-email-presets">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyPreset(p)}
            className="rounded-full border px-3 py-1 text-xs hover:shadow-sm transition"
            style={{ borderColor: p.accent, color: p.accent, background: "#fff" }}
            data-testid={`admin-email-preset-${p.id}`}
          >
            {p.label} → {p.to}
          </button>
        ))}
      </div>

      <div
        className="wofy-card grid md:grid-cols-2 gap-4 p-5"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <label className="block text-xs">
          <span className="opacity-70">Recipient</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="support@indianfamilytree.com"
            className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="admin-email-to"
          />
        </label>
        <label className="block text-xs">
          <span className="opacity-70">Brand label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="IndianFamilyTree.com"
            className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="admin-email-label"
          />
        </label>
        <label className="block text-xs">
          <span className="opacity-70">Accent colour</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="h-9 w-12 rounded border"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="admin-email-accent"
            />
            <input
              type="text"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border text-sm bg-transparent font-mono"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </div>
        </label>
        <label className="block text-xs">
          <span className="opacity-70">Subject (optional)</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="[Test] Deliverability check"
            className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="admin-email-subject"
          />
        </label>
        <label className="block text-xs md:col-span-2">
          <span className="opacity-70">Note (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Include any context to help the recipient identify the test — run id, purpose, etc."
            className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="admin-email-note"
          />
        </label>
        <div className="md:col-span-2 flex justify-end">
          <button
            type="button"
            onClick={send}
            disabled={busy || !to.trim()}
            className="wofy-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="admin-email-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send test email
          </button>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-6" data-testid="admin-email-history">
          <div className="overline mb-2">RECENT TESTS · THIS SESSION</div>
          <div className="wofy-card divide-y" style={{ borderColor: "var(--wofy-border)" }}>
            {history.map((h, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 py-2.5 px-1 text-xs">
                <div className="min-w-0">
                  <div className="font-medium truncate">{h.to}</div>
                  <div className="opacity-60 truncate">{h.subject}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase ${h.status === "sent" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}
                  >
                    {h.status}
                  </span>
                  {h.resend_id && (
                    <button
                      type="button"
                      onClick={() => copyId(h.resend_id)}
                      className="inline-flex items-center gap-1 font-mono opacity-70 hover:opacity-100"
                      title="Copy Resend message id"
                    >
                      {copiedId === h.resend_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {h.resend_id.slice(0, 8)}…
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
